/**
*
* TopicMessage
*
*/

import React, { useReducer, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';
import { Button, ListGroupItem } from 'react-bootstrap';
import useCurrentRole from 'containers/User/useCurrentRole.js';
import Avatar from 'components/media/Avatar/index.jsx';
import UserText from 'components/media/UserText/Loadable.js';
import Preview from 'components/media/Preview/index.jsx';
import EditDeleteControl from 'components/interaction/EditDeleteControl/index.jsx';
import UserModal from 'components/user/UserModal/index.jsx';
import PostDate from 'components/Post/PostDate/index.jsx';
import { getIdNumber } from 'utils/userIdRole.js';
import sameRole from 'utils/sameRole.js';
import useTranslation from 'utils/useTranslation.jsx';
import useApi3 from 'utils/useApi3/index.js';
import useRecoveredData from 'utils/useRecoveredData.js';
import messages from '../messages.js';
import InlineEdit from '../InlineEdit/index.jsx';
import { MESSAGE_TYPE_META } from '../constants.jsx';
import useDeleteMessage from './useDeleteMessage.js';
import MessageStats from './MessageStats.jsx';
import useConversationsContext from '../useConversationsContext.js';

const WithMargin = styled.div`
  @media screen {
    &.mright {
      margin-right: 60px;
    }
    &.mleft {
      margin-left: 60px;
    }
  }
  @media print {
    padding-left: 0 !important;
    padding-right: 0 !important;
  }

`;

function TopicMessage(props) {
  const { message, topic, participants, markText, editing, setEditing } = props;
  const role = useCurrentRole();
  const { baseUrl, inbox, setInTopics } = useConversationsContext();
  const setTranslation = (msg) => setInTopics([topic.id, 'messages', message.id], msg);
  const url = topic.id && `${baseUrl}/${topic.id}/messages/${message.id}/translation`;
  const getTranslation = useApi3({ url, skipLoading: true, skipParseDates: true }, setTranslation);

  const { inboxId } = useParams();
  const [showUser, toggleShowUser] = useReducer((f) => !f, false);
  const deleteMessage = useDeleteMessage(topic.id);
  const { createdBy, body, media, deleted, id, messageType, permissions, statistics, translation } = message;
  const [storedMessage] = useRecoveredData('topics', inboxId, 'msg', topic.id, id);
  const [editPost, setEditPost] = useState(storedMessage);
  const { dir, element } = useTranslation(translation, { className: 'small mt-3 mb-0 px-0 py-1 bg-transparent border-0 opacity-50', onTranslate: getTranslation });

  function handleEditPost(msg) {
    setEditPost(msg);
    setEditing(msg !== null);
  }

  const handleDelete = permissions?.delete ? () => deleteMessage(id) : undefined;
  const handleEdit = permissions?.edit ? () => handleEditPost({ ...message, topicId: topic.id }) : undefined;

  if (messageType === MESSAGE_TYPE_META) {
    return (
      <WithMargin as={ListGroupItem} className="border-0 text-center mright mleft print-border-bottom" tabIndex="0">
        <UserText inline className="text-muted small">{body}</UserText>
      </WithMargin>
    );
  }

  const className = classNames('rounded-3 px-4 py-3 text-dark', sameRole(role, message.createdBy) ? 'mleft bg-gray' : 'mright shaded-light');
  const creatorId = getIdNumber(createdBy);
  return (
    <ListGroupItem id={id} className="border-0 print-border-bottom" tabIndex="0">
      {editPost ? (
        <div className={className}>
          <InlineEdit post={editPost} onClose={() => handleEditPost(null)} />
        </div>
      ) : (
        <WithMargin className={className}>
          <div className="d-flex align-items-center mb-2">
            <Avatar src={createdBy.avatar} bsSize="small" className="me-2" />
            <div className="flex-grow-1">
              <Button variant="link" className="p-0 m-0 flex-grow-1" onClick={toggleShowUser}>
                <UserText inline className="small fw-semibold me-2 flex-grow-1" markText={markText}>{createdBy.displayName}</UserText>
              </Button>
            </div>
            {!inbox.archived && <EditDeleteControl className="float-end" disabled={editing} onEdit={(handleEdit)} onDelete={handleDelete} confirmDeleteText={messages.confirmDelete} ifEnabled />}
          </div>
          {
            deleted ? <div className="text-muted font-italic small"><FormattedMessage {...messages.deletedMessage} /></div>
              : (
                <div dir={dir}>
                  <UserText markText={markText}>{body}</UserText>
                  <Preview files={media} small gallery />
                </div>
              )
          }
          <div className="d-flex justify-content-between align-items-baseline">
            <div>{element}</div>
            <span>
              <PostDate className="small text-muted" post={message} />
              <MessageStats stats={statistics} participants={participants} className="pe-0" />
            </span>
          </div>
        </WithMargin>
      )}
      {showUser && <UserModal userId={creatorId} onHide={toggleShowUser} />}
    </ListGroupItem>
  );
}

TopicMessage.propTypes = {
  message: PropTypes.object.isRequired,
  topic: PropTypes.object,
  markText: PropTypes.string,
  participants: PropTypes.object,
  editing: PropTypes.bool,
  setEditing: PropTypes.func,
};

export default TopicMessage;
